import { isEmpty } from '@ember/utils'
import fillMonths from 'district-ui-client/utils/student-events/fill-months'
import type StudentEventsService from 'district-ui-client/services/student-events'
import type { StatsScope } from 'district-ui-client/utils/student-events/stats-scope'
import type { NormalizedInterval } from 'date-fns'

export interface AssessmentScoresTotalsResponse {
  assessment_scores_totals_summary_by_grade: SummaryData[]
  assessment_scores_totals_by_grade_by_month: MonthData[]
}

export interface SummaryData {
  grade_position: number
  data: [{ category: string; count: number }, { category: string; count: number }]
}

export interface MonthData {
  grade_position: number
  months: { month: string; count: number }[]
}

export interface AssessmentScoresTotalsData {
  grade_position: number
  summary: { student_count: number; total_count: number }
  months: { count: number; month: string }[]
}

export async function assessmentScoresTotals(
  this: StudentEventsService,
  product: LegacyProductMs | LegacyProductRe | LegacyProductRex,
  statsScope: StatsScope,
  period: NormalizedInterval,
): Promise<Nullable<AssessmentScoresTotalsData[]>> {
  if (isEmpty(statsScope.ids)) return null

  const url = this.buildUrl('assessment_scores_totals_by_grade_by_month', product, statsScope)
  const query = this.buildQuery(statsScope, period)

  const data = await this.fetchData<AssessmentScoresTotalsResponse>(url, query)
  if (!data) return null

  const {
    assessment_scores_totals_summary_by_grade: summaryData,
    assessment_scores_totals_by_grade_by_month: monthData,
  } = data

  if (summaryData.length === 0 || monthData.length === 0) return null

  return groupByGrade(summaryData, monthData, period)
}

function groupByGrade(
  summaryData: SummaryData[],
  monthData: MonthData[],
  period: NormalizedInterval,
): AssessmentScoresTotalsData[] {
  const sortedSummaryData = [...summaryData].sort((a, b) => a.grade_position - b.grade_position)

  return sortedSummaryData
    .map((summary) => {
      const gradeMonthData = monthData.find((month) => month.grade_position === summary.grade_position)
      if (!gradeMonthData) return null

      const student_count = summary.data.find((value) => value.category === 'student_count')?.count ?? 0
      const total_count = summary.data.find((value) => value.category === 'total_count')?.count ?? 0
      const fullMonthData = fillMonths(gradeMonthData.months, period, {
        count: 0,
      })

      return {
        grade_position: summary.grade_position,
        summary: {
          student_count,
          total_count,
        },
        months: fullMonthData,
      }
    })
    .filter((data) => data !== null)
}
